import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A small curated watchlist that spans crypto, stocks, metals, forex.
const WATCH = [
  { s: "BTC", kind: "crypto", label: "BTC" },
  { s: "ETH", kind: "crypto", label: "ETH" },
  { s: "SOL", kind: "crypto", label: "SOL" },
  { s: "AAPL", kind: "stock", label: "AAPL" },
  { s: "NVDA", kind: "stock", label: "NVDA" },
  { s: "TSLA", kind: "stock", label: "TSLA" },
  { s: "GOLD", kind: "metal", label: "GOLD" },
  { s: "SILVER", kind: "metal", label: "SILVER" },
  { s: "EURUSD", kind: "fx", label: "EUR/USD" },
  { s: "USDJPY", kind: "fx", label: "USD/JPY" },
];

function fmtPrice(kind, price) {
  if (price == null) return "—";
  if (kind === "fx") return Number(price).toFixed(4);
  if (price >= 1000) return Number(price).toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (price >= 1) return Number(price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return Number(price).toPrecision(4);
}

/**
 * Streaming price ticker — one long marquee that fetches /markets/top + /stocks + /macro
 * every 8 seconds and animates continuously via CSS keyframes.
 * Prefers `coins` and `stocks` passed in via props (already loaded upstream) to save requests.
 */
export default function LivePriceTicker({ coins = [], stocks = [] }) {
  const [macro, setMacro] = useState(null);
  const [tick, setTick] = useState(0);
  const timer = useRef(null);

  // pull metals + forex from /macro (cheap combined endpoint)
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios
        .get(`${API}/markets/macro`)
        .then(({ data }) => { if (!cancelled) setMacro(data); })
        .catch(() => {});
    };
    load();
    timer.current = setInterval(() => {
      if (document.visibilityState === "visible") { load(); setTick((n) => n + 1); }
    }, 8000);
    return () => { cancelled = true; clearInterval(timer.current); };
  }, []);

  const rows = useMemo(() => {
    const coinMap = new Map(coins.map((c) => [String(c.symbol).toUpperCase(), c]));
    const stockMap = new Map(stocks.map((s) => [s.symbol, s]));
    const metals = (macro?.metals || []).reduce((m, r) => { m.set(r.symbol, r); return m; }, new Map());
    const fx = (macro?.forex || []).reduce((m, r) => { m.set(r.symbol, r); return m; }, new Map());
    return WATCH.map((w) => {
      let price = null;
      let chg = null;
      if (w.kind === "crypto") {
        const c = coinMap.get(w.s);
        price = c?.price; chg = c?.change_24h;
      } else if (w.kind === "stock") {
        const s = stockMap.get(w.s);
        price = s?.price; chg = s?.change_pct;
      } else if (w.kind === "metal") {
        const m = metals.get(w.s);
        price = m?.price; chg = m?.change_pct;
      } else if (w.kind === "fx") {
        const f = fx.get(w.s);
        price = f?.price; chg = f?.change_pct;
      }
      return { ...w, price, chg };
    }).filter((r) => r.price != null);
  }, [coins, stocks, macro, tick]);

  if (!rows.length) return null;

  // duplicate the row set so the marquee wraps seamlessly
  const doubled = [...rows, ...rows];

  return (
    <div
      className="relative overflow-hidden border-y border-white/10 bg-ink/60 py-3"
      data-testid="live-price-ticker"
      aria-label="Live watchlist prices"
    >
      <div className="absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-ink to-transparent" />
      <div className="absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-ink to-transparent" />
      <div className="flex whitespace-nowrap will-change-transform aureon-marquee">
        {doubled.map((r, i) => {
          const up = (r.chg ?? 0) >= 0;
          return (
            <div key={`${r.s}-${i}`} className="mx-6 flex shrink-0 items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">{r.label}</span>
              <span className="font-tabular text-sm text-white/85">
                {r.kind === "fx" ? "" : "$"}
                {fmtPrice(r.kind, r.price)}
              </span>
              <span
                className={`font-tabular text-[10px] ${up ? "text-[#26A69A]" : "text-[#EF5350]"}`}
              >
                {up ? "▲" : "▼"}{Math.abs(r.chg ?? 0).toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>
      <style>{`
        .aureon-marquee {
          animation: aureon-marquee-scroll 40s linear infinite;
        }
        @keyframes aureon-marquee-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
